import { type NetPriceData, type TotalPriceData, type WithSurcharge } from "~/domains/pricing/types/PriceData";
import { type ReactNode } from "react";
import { useTranslation, type WithTranslation } from "react-i18next";
import { obtainRebateLabel, RebateLabelType } from "~/domains/pricing/components/Price/v1/Price";
import classNames from "classnames";
import { formatCurrency } from "~/common/utils/currencyFormatter";

import { isNonNull } from "~/common/utils/reducers";
import { isTariffSurchargeEnabled } from "~/features/cart/cart/utils/cartUtils";

export const SPECIAL_PRICE_CODE = "special";
export const REBATE_CODE = "rebate";

export function isRebateLabelVisible(price: NetPriceData | null | undefined) {
  if (!price) {
    return false;
  }
  return !price.isSubscription && !price.surcharge && !price.isSpecial && price.rebate;
}

export function isSpecialPriceLabelVisible(price: NetPriceData | null | undefined): boolean {
  return !!price?.isSpecial;
}
export function hasSurcharge(price: NetPriceData | null | undefined): boolean {
  return !!price?.surcharge;
}

interface NetPriceLabels {
  price: ReactNode;
  unit: ReactNode;
  label: ReactNode;
  fullLabel: ReactNode;
  extraLabels: { full: ReactNode; short: ReactNode; code: string }[];
}

export function obtainNetPriceLabels(
  price: NetPriceData | null | undefined,
  t: WithTranslation["t"],
  { zeroText = "-", surchargeEnabled = true }: { zeroText?: ReactNode; surchargeEnabled?: boolean } = {},
): NetPriceLabels {
  if (!price || !price.value) {
    return {
      price: zeroText,
      unit: "",
      label: "",
      fullLabel: "",
      extraLabels: [],
    };
  }

  const hasSurcharge = surchargeEnabled && price.surcharge;
  const tariffSurcharge = isTariffSurchargeEnabled() && price.tariffSurcharge;
  // TODO: This can be just price.value.
  const val = hasSurcharge ? price.value : price.baseValue;

  const result: Partial<NetPriceLabels> = {
    price: formatCurrency(val, price.currencyIso) + (hasSurcharge || tariffSurcharge ? "*" : ""),
    extraLabels: [],
  };
  if (price.isSubscription) {
    result.unit = t("common.product.perMonth");
  } else {
    result.unit = t("common.product.perPiece", { count: price.unit });
  }
  result.label = t("cartPage.table.header.price.title");
  result.fullLabel = [result.label, result.unit].filter(isNonNull).join(" ");
  if (isSpecialPriceLabelVisible(price)) {
    const label = t("addProduct.salesman.priceTable.specialPrice");
    result.extraLabels!.push({
      full: label,
      short: label,
      code: SPECIAL_PRICE_CODE,
    });
  }
  if (isRebateLabelVisible(price)) {
    result.extraLabels!.push({
      full: obtainRebateLabel(RebateLabelType.Full, price.rebate!, t),
      short: obtainRebateLabel(RebateLabelType.Default, price.rebate!, t),
      code: REBATE_CODE,
    });
  }
  return result as NetPriceLabels;
}

interface TotalPriceLabels {
  total: ReactNode;
  unit: ReactNode;
  label: ReactNode;
  fullLabel: ReactNode;
}

export function obtainTotalPriceLabels(
  total: (TotalPriceData & WithSurcharge) | null | undefined,
  t: WithTranslation["t"],
  config?: { zeroText?: ReactNode; surchargeEnabled?: true },
): TotalPriceLabels;

export function obtainTotalPriceLabels(
  total: TotalPriceData | null | undefined,
  t: WithTranslation["t"],
  config: { zeroText?: ReactNode; surchargeEnabled: false },
): TotalPriceLabels;

export function obtainTotalPriceLabels(
  total: (TotalPriceData & WithSurcharge) | TotalPriceData | null | undefined,
  t: WithTranslation["t"],
  { zeroText = "-", surchargeEnabled = true }: { zeroText?: ReactNode; surchargeEnabled?: boolean } = {},
): TotalPriceLabels {
  if (!total || !total.value) {
    return {
      total: zeroText,
      unit: "",
      label: "",
      fullLabel: "",
    };
  }

  if (surchargeEnabled && !("baseValue" in total)) {
    throw new Error("TODO");
  }
  const hasSurcharge = surchargeEnabled && (total as TotalPriceData & WithSurcharge).surcharge;
  let val;
  if (!surchargeEnabled && "baseValue" in total) {
    val = total.baseValue;
  } else {
    val = total.value;
  }

  const result: Partial<TotalPriceLabels> = {
    total: formatCurrency(val, total.currencyIso) + (hasSurcharge ? "*" : ""),
  };
  if (total.isSubscription) {
    result.unit = t("common.product.perYear");
  }
  result.label = t("common.product.totalValue");
  result.fullLabel = [result.label, result.unit].filter(isNonNull).join(" ");

  return result as TotalPriceLabels;
}

type NetPriceProps = {
  className?: string;
  classes?: {
    root?: string;
    price?: string;
    unit?: string;
    extraLabel?: string;
  };
  extraLabelsEnabled?: "full" | "short" | false;
  price: Parameters<typeof obtainNetPriceLabels>[0];
  showUnit?: boolean;
} & Parameters<typeof obtainNetPriceLabels>[2];
export const NetPrice = ({
  price,
  showUnit = true,
  surchargeEnabled = true,
  extraLabelsEnabled = "full",
  zeroText,
  classes: externalClasses = {},
  className,
}: NetPriceProps) => {
  const { t } = useTranslation();
  const labels = obtainNetPriceLabels(price, t, { surchargeEnabled, zeroText });

  return (
    <div className={classNames(externalClasses.root, className)}>
      <span data-test-id={"value"} className={externalClasses.price}>
        {labels.price}
      </span>
      {showUnit && labels.unit && (
        <span data-test-id={"unit"} className={classNames("f-label-color", externalClasses.unit)}>
          {labels.unit}
        </span>
      )}
      {extraLabelsEnabled && (
        <div>
          {labels.extraLabels.map((label, i) => (
            <div key={i}>{label[extraLabelsEnabled]}</div>
          ))}
        </div>
      )}
    </div>
  );
};

type TotalNetPriceProps = {
  className?: string;
  classes?: {
    root?: string;
    price?: string;
    unit?: string;
  };
  showUnit?: boolean;
  price: TotalPriceData | null | undefined;
  zeroText?: ReactNode;
  surchargeEnabled?: boolean;
};
export const TotalNetPrice = ({
  price,
  showUnit = true,
  surchargeEnabled = true,
  zeroText,
  classes: externalClasses = {},
  className,
}: TotalNetPriceProps) => {
  const { t } = useTranslation();
  let labels;
  if (surchargeEnabled) {
    labels = obtainTotalPriceLabels(price as TotalPriceData & WithSurcharge, t, { surchargeEnabled: true, zeroText });
  } else {
    labels = obtainTotalPriceLabels(price, t, { surchargeEnabled: false, zeroText });
  }

  return (
    <div className={classNames(externalClasses.root, className)}>
      <span data-test-id={"value"} className={externalClasses.price}>
        {labels.total}
      </span>
      {showUnit && labels.unit && (
        <span data-test-id={"unit"} className={classNames("f-label-color", externalClasses.unit)}>
          {labels.unit}
        </span>
      )}
    </div>
  );
};
